import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { contentApi } from '../../../api/swagger'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { FetchLearningCenterOptionsProgramTypeEnum } from '../../../swagger'
import {
  FilterFileByChallengeLevel,
  LearningCenterTabs,
} from '../../../utils/searchAndFilterEnums'
import { useLearningCenterContext } from '../../Context/LearningCenterContext'
import { useAuth } from '../../Routes/AuthProvider'

export const stringToChallengeLevelMap = new Map<
  string,
  FilterFileByChallengeLevel
>([
  ['Challenge A', FilterFileByChallengeLevel.ChallengeA],
  ['Challenge B', FilterFileByChallengeLevel.ChallengeB],
  ['Challenge I', FilterFileByChallengeLevel.Challenge1],
  ['Challenge II', FilterFileByChallengeLevel.Challenge2],
  ['Challenge III', FilterFileByChallengeLevel.Challenge3],
  ['Challenge IV', FilterFileByChallengeLevel.Challenge4],
])

export const useLearningCenterOptions = (options: {
  /** If not provided, don't call the endpoint. */
  programType?: FetchLearningCenterOptionsProgramTypeEnum
}): { isLoading: boolean } => {
  const { programType } = options
  const { t } = useTranslation()
  const { featureAbility } = useAuth()
  const { handleError } = useAppSnackbar()

  const {
    updateLearningCenterOptions,
    optionsLoadingId: loadingId,
    level,
    selectedTabKey,
    welcomeCenterContent,
    updateCycle,
  } = useLearningCenterContext()
  const isChallengeTab = selectedTabKey.includes(LearningCenterTabs.Challenge)
  const hasLearningCenterAccess = featureAbility.can(
    'learningCenter',
    'Feature'
  )
  const fetchLearningCenterOptions = async () => {
    if (
      (level === undefined && !welcomeCenterContent && isChallengeTab) ||
      !programType ||
      selectedTabKey === LearningCenterTabs.Playlist
    )
      return

    const programTypeValue = (
      isChallengeTab ? level : programType
    ) as FetchLearningCenterOptionsProgramTypeEnum

    try {
      const { categories, filetypes, cycles } =
        await contentApi.fetchLearningCenterOptions({
          programType: programTypeValue,
        })

      /**
       * Update the options within the LearningCenterContext. No need to have state
       * variables within this hook since our context will hold the information
       * for child components.
       */
      updateLearningCenterOptions({
        categories,
        filetypes,
        cycles,
      })
      const defaultCycle = cycles?.find((cycle) => cycle.isDefault)?.cycle ?? 0
      updateCycle(defaultCycle)
    } catch (e) {
      await handleError(
        e,
        t(
          'Endpoints.FetchLearningCenterOptions.Error',
          'An unknown error occurred fetching learning center options.'
        )
      )
    }
  }
  const {
    triggerFetch: fetchLearningCenterTrigger,
    removeLoadingIds,
    isLoading,
  } = useLoadingContext({
    loadingId,
    asyncFunction: fetchLearningCenterOptions,
  })

  useEffect(() => {
    /** Don't call the endpoint if we can't access the feature */
    if (
      (!!programType || (!!level && isChallengeTab)) &&
      !welcomeCenterContent &&
      hasLearningCenterAccess
    ) {
      fetchLearningCenterTrigger()
    }

    removeLoadingIds([loadingId])
  }, [
    hasLearningCenterAccess,
    welcomeCenterContent,
    programType,
    level,
    isChallengeTab,
    fetchLearningCenterTrigger,
    removeLoadingIds,
    loadingId,
  ])

  return { isLoading }
}
